// ============================================================
// Module registry — V2 platform plumbing.
//
// R2 (2026-04-23) — Inventory canary.
//   Owner: principal-architect (design) + firebase-expert (impl).
//   See ARCHITECTURE.md §2 (manifest format) + §3 (loader strategy).
//   See DESIGN_LOG R2 entry.
//
// What this is for
// ────────────────
// Today V2 hardcodes every route in `src/main.js _routes`. Per §3,
// V2 needs to be MODULE-PLUGGABLE so we can:
//   - turn modules off per tenant (a Trading customer ≠ Manufacturing),
//   - dynamic-import each module so its bytes only ship when enabled,
//   - generate Security Rules / role permissions / dashboard widgets
//     from the same source-of-truth as the routes themselves.
//
// What this is NOT for (yet)
// ──────────────────────────
//   - dependency resolution beyond a flat enabled-list (R4),
//   - dashboard widget composition per role (R3+R4),
//   - Cloud Functions auto-registration per manifest (R6),
//   - Rules generation from `permissions` strings (R6).
//
// R2 deliberately implements the smallest surface that lets the
// inventory module be discovered by manifest while every other
// module continues to work via the legacy hardcoded path.
// ============================================================

/**
 * @typedef ModuleRoute
 * @prop {string}   id        kebab-case key (also the URL hash). Stable.
 * @prop {string}   label     human label
 * @prop {string}   icon      emoji
 * @prop {string}   section   sidebar section key (main | procure | sell | finance | people | account | admin | _hidden)
 * @prop {number}  [sidebarPosition]  ordering WITHIN the section (lower = higher
 *   in the list). Specified in ARCHITECTURE.md §2 since R2 but never implemented
 *   until 2026-09-21 — before that, sidebar order was Object.entries(_routes)
 *   insertion order, i.e. module-load order (see the determinism fix in
 *   loadEnabledModules). Convention: multiples of 10, leaving room to insert.
 *   Routes without a number sort last (999) and tie-break alphabetically by
 *   route id, so an unnumbered route is still deterministic. See main.js
 *   renderApp() for the comparator.
 *   GOTCHA: a route id declared by TWO manifests (invoicing vs sales both
 *   declare `customers` + `customer-invoices`, resolved by supersededBy) MUST
 *   carry the same number in both, or the order flips per tenant.
 * @prop {(role: string, hasPerm: (perm: string) => boolean) => boolean} visible  shown in the sidebar?
 * @prop {(container: HTMLElement, ctx: object) => void}  mount
 * @prop {() => void}  unmount
 */

/**
 * @typedef ModuleManifest
 * @prop {string} id              unique kebab-case id, also used in tenantConfig.enabledModules
 * @prop {string} name            human label (locale override later)
 * @prop {string} version         semver — major bump = breaking change
 * @prop {string} [description]
 * @prop {() => Promise<object>}  load     dynamic import returning the module body
 * @prop {ModuleRoute[]}          routes   sidebar contributions
 * @prop {string[]}              [schemas]            Firestore collection names
 * @prop {string[]}              [permissions]        permission strings (R6)
 * @prop {string[]}              [dependencies]       other module ids required first
 * @prop {string[]}              [supersededBy]       A2 (2026-04-25) — when any
 *   id in this list is also enabled, THIS module's routes are dropped from
 *   the sidebar (the superseder provides them + more). Asymmetric: only the
 *   superseded side declares it. See getRoutesFor() and DESIGN_LOG A2.
 * @prop {object[]}              [dashboardWidgets]   widget descriptors (R3+R4)
 * @prop {string[]}              [fitsIndustries]
 */

export class ModuleRegistry {
  constructor() {
    /** @type {Object<string, ModuleManifest>} */
    this._manifests = {};
    /** @type {Object<string, object>} */
    this._loadedBodies = {};
    /** @type {Object<string, ModuleRoute>} */
    this._activeRoutes = {};
  }

  /**
   * Register a manifest. Idempotent — re-registering the same id replaces
   * the existing manifest (used during HMR; production loads each once).
   */
  register(manifestId, manifest) {
    if (!manifestId) throw new Error('register: manifestId required');
    if (!manifest || manifest.id !== manifestId) {
      throw new Error(`register: manifest.id (${manifest && manifest.id}) must match registration key (${manifestId})`);
    }
    if (!Array.isArray(manifest.routes)) {
      throw new Error(`register(${manifestId}): manifest.routes must be an array`);
    }
    this._manifests[manifestId] = manifest;
  }

  /** Look up a registered manifest by id (or undefined). */
  getManifest(id) {
    return this._manifests[id];
  }

  /**
   * Load module bodies for the given enabled-module ids.
   *   - Validates dependencies — throws on unmet dep for any ENABLED+REGISTERED
   *     module (better to fail boot than ship partial functionality that lies
   *     to the user).
   *   - Calls each manifest's load() in parallel — these are dynamic
   *     `import()` calls, so Vite emits separate chunks.
   *   - Resolved module bodies are stashed for getRoutesFor() to bind
   *     mount/unmount handlers against.
   *
   * R3-followup (2026-04-24) — strict mode:
   *   - Unknown ids in enabledIds are WARNED-loud (`console.warn`) instead of
   *     silently skipped. A tenant with one stale entry still boots, but the
   *     telemetry signal is unmistakable. Use `getUnknownModules()` from the
   *     tenant-config UI to surface stale entries to admins.
   *   - Unmet dependencies on registered modules THROW — partial functionality
   *     is worse than a clear boot error.
   *
   * @param {string[]} enabledIds  module ids enabled for the current tenant
   * @param {object}   ctx         { user, role, tenantId } — passed to manifests if they need it (unused today)
   * @returns {Promise<{ loaded: string[], skipped: string[] }>}
   */
  async loadEnabledModules(enabledIds, _ctx = {}) {
    const ids = Array.isArray(enabledIds) ? enabledIds.slice() : [];

    // 1. Dependency validation — registered-only (skipped manifests have no deps to check yet).
    //    R3-followup: this now throws clearly with the dep name AND surfacing
    //    the offending module id, so the boot error tells the admin exactly
    //    which entry to add to enabledModules.
    for (const id of ids) {
      const m = this._manifests[id];
      if (!m) continue;  // unknown id is handled in step 2 (warn-loud + skipped[])
      const deps = Array.isArray(m.dependencies) ? m.dependencies : [];
      for (const dep of deps) {
        if (!ids.includes(dep)) {
          throw new Error(
            `Module "${id}" requires "${dep}" but it is not enabled in tenantConfig.enabledModules. ` +
            `Add "${dep}" to enabledModules and reload, or disable "${id}".`
          );
        }
      }
    }

    // 2. Parallel dynamic-imports for the registered + enabled subset.
    //    Unknown ids are warned-loud — they typically indicate a stale tenant
    //    config (module renamed, removed, or never existed because of a typo).
    const loaded = [];
    const skipped = [];
    const tasks = [];
    for (const id of ids) {
      const m = this._manifests[id];
      if (!m) {
        console.warn('[registry] Unknown module in enabledModules: "' + id + '" — skipping. ' +
          'Was this module renamed or removed? Edit it in Settings → Platform Modules.');
        skipped.push(id);
        continue;
      }
      // 2026-09-21 DETERMINISM FIX — `loaded` records the ENABLE ORDER, not
      // the network-resolution order. It used to be pushed inside .then(),
      // so `loaded` (and therefore the step-3 route-binding order, and
      // therefore Object.entries(_routes) in main.js renderApp) was ordered
      // by whichever dynamic import() happened to resolve first. That is
      // chunk-size / cache / RTT dependent: the same tenant could see the
      // sidebar in a different order on a cold load than on a warm one.
      // Pushing synchronously here makes the order a pure function of
      // tenantConfig.enabledModules. Promise.all() below still waits for
      // every body, and a rejected load still throws before step 3 runs,
      // so no id can reach the binding loop without its body present.
      loaded.push(id);
      tasks.push(
        m.load().then(body => {
          this._loadedBodies[id] = body;
        }).catch(err => {
          console.error(`[registry] module "${id}" failed to load:`, err);
          throw new Error(`Failed to load module "${id}": ${err.message}`);
        })
      );
    }
    await Promise.all(tasks);

    // 3. Bind routes — for each enabled+registered module, resolve mount/unmount
    //    by string-name lookup against the loaded body, OR accept inline
    //    functions in the manifest. (Inline is preferred in R2 because
    //    it preserves the per-route closure — useful for dashboard's
    //    onJumpToInventory wiring later.)
    for (const id of loaded) {
      const m = this._manifests[id];
      const body = this._loadedBodies[id];
      for (const route of m.routes) {
        const mountFn = (typeof route.mount === 'function')
          ? route.mount
          : (body && body[route.mount]);
        const unmountFn = (typeof route.unmount === 'function')
          ? route.unmount
          : (body && body[route.unmount]);
        if (typeof mountFn !== 'function') {
          throw new Error(
            `Module "${id}" route "${route.id}": mount is neither a function ` +
            `nor a string matching an export of the loaded body.`
          );
        }
        this._activeRoutes[route.id] = {
          ...route,
          moduleId: id,
          mount:   mountFn,
          unmount: typeof unmountFn === 'function' ? unmountFn : (() => {})
        };
      }
    }

    return { loaded, skipped };
  }

  /**
   * Return a route map { routeId: routeObject } for the given enabled ids.
   * Caller (main.js) merges this into its existing _routes object.
   * R2 usage: only inventory is in the returned map; main.js still owns
   * dashboard / suppliers / requisitions / etc. as hardcoded entries.
   *
   * A2 (2026-04-25) — `supersededBy` filter.
   * ───────────────────────────────────────
   * A manifest may declare `supersededBy: ['otherModuleId']`. When BOTH
   * the module AND its superseder are in `enabledIds`, the module's
   * routes are DROPPED. The superseder is taken to provide the same
   * routes plus more. This is the "sales supersedes invoicing" case:
   * a tenant with both enabled sees sales' Customers + Customer Invoices
   * (the richer set), not two duplicate sidebar entries. See DESIGN_LOG
   * A2 sub-decision 2.
   *
   * The check is asymmetric — only the SUPERSEDED side declares the
   * relationship. The superseder doesn't need to know about the
   * superseded (principle of least coupling).
   *
   * @param {string[]|null} enabledIds  module ids enabled for the current
   *   tenant. null = return all activeRoutes.
   * @returns {Object<string, ModuleRoute>}
   */
  getRoutesFor(enabledIds = null) {
    if (!enabledIds) return { ...this._activeRoutes };
    const ids = new Set(enabledIds);

    // Build the "suppressed" set — module ids whose routes should be
    // hidden because a supersederLocally for them is also enabled.
    // Iterate registered manifests (not all of which are enabled) so we
    // see the supersededBy declarations.
    const suppressed = new Set();
    for (const id of ids) {
      const m = this._manifests[id];
      if (!m || !Array.isArray(m.supersededBy)) continue;
      for (const supersederId of m.supersededBy) {
        if (ids.has(supersederId)) {
          // The superseder is also enabled. This module's routes are
          // covered. Suppress them.
          suppressed.add(id);
          break;
        }
      }
    }

    const out = {};
    for (const [routeId, route] of Object.entries(this._activeRoutes)) {
      if (!ids.has(route.moduleId)) continue;
      if (suppressed.has(route.moduleId)) continue;
      out[routeId] = route;
    }
    return out;
  }

  /**
   * Return dashboardWidget descriptors contributed by enabled modules,
   * filtered by role. R2 placeholder — returns empty until R3+R4 wire it
   * into the dashboard composition layer.
   *
   * Access-lifecycle (2026-07-15) — `opts.isStopped` short-circuits to an
   * EMPTY list. A stopped staff must not discover any operational widget:
   * every widget fires reads that firestore.rules now denies for a stopped
   * user (memberCanRead / hasPerm both fold in !isStopped()), so mounting
   * them would only surface a wall of permission errors. The dashboard
   * page renders a purpose-built stopped panel instead. `opts` is an
   * OPTIONAL 3rd arg — existing 2-arg callers are unaffected.
   *
   * @param {string[]} enabledIds
   * @param {string}   role
   * @param {{isStopped?: boolean}} [opts]
   */
  getDashboardWidgetsFor(enabledIds = [], role = 'staff', opts = {}) {
    if (opts && opts.isStopped) return [];
    const ids = new Set(enabledIds);
    const out = [];
    for (const id of ids) {
      const m = this._manifests[id];
      if (!m || !Array.isArray(m.dashboardWidgets)) continue;
      for (const w of m.dashboardWidgets) {
        const allow = !w.defaultRoles || w.defaultRoles.includes(role);
        if (allow) out.push({ ...w, moduleId: id });
      }
    }
    return out;
  }

  /**
   * Aggregate permissions declared by enabled modules. R6 will turn this
   * into the role→permission resolution layer; R2 just exposes the union.
   */
  getPermissionsFor(enabledIds = []) {
    const ids = new Set(enabledIds);
    const set = new Set();
    for (const id of ids) {
      const m = this._manifests[id];
      if (!m || !Array.isArray(m.permissions)) continue;
      for (const p of m.permissions) set.add(p);
    }
    return Array.from(set);
  }

  /**
   * Return the ids in enabledIds that are NOT registered with the registry.
   * Used by the tenant-config UI (Settings → Platform Modules) to surface
   * stale entries as "⚠ Stale: [id] — was this module renamed or removed?"
   *
   * @param {string[]} enabledIds
   * @returns {string[]}  unknown ids, preserving input order
   */
  getUnknownModules(enabledIds = []) {
    const ids = Array.isArray(enabledIds) ? enabledIds : [];
    return ids.filter((id) => !this._manifests[id]);
  }

  /**
   * Return ALL registered manifests as a plain array. Used by the tenant-
   * config UI to render the "all available modules" list (vs only the
   * enabled subset).
   *
   * @returns {ModuleManifest[]}
   */
  getAllManifests() {
    return Object.values(this._manifests);
  }

  /**
   * For a given module id, return the list of OTHER currently-enabled
   * modules that depend on it (i.e. the modules that would break if this
   * one were disabled). Used by the tenant-config UI's dependency-guard.
   *
   * @param {string}   id          the module being considered for disable
   * @param {string[]} enabledIds  current enabledModules list
   * @returns {string[]}  ids of dependents that are currently enabled
   */
  getDependentsOf(id, enabledIds = []) {
    if (!id) return [];
    const enabledSet = new Set(enabledIds);
    const dependents = [];
    for (const m of Object.values(this._manifests)) {
      if (m.id === id) continue;
      if (!enabledSet.has(m.id)) continue;  // skip not-currently-enabled
      const deps = Array.isArray(m.dependencies) ? m.dependencies : [];
      if (deps.includes(id)) dependents.push(m.id);
    }
    return dependents;
  }

  /**
   * Aggregate the schemas (collection names) declared by enabled modules.
   * Used today by data-architect tooling (parity matrix); future use:
   * Rules generation, index hint generation.
   */
  getSchemasFor(enabledIds = []) {
    const ids = new Set(enabledIds);
    const out = [];
    for (const id of ids) {
      const m = this._manifests[id];
      if (!m || !Array.isArray(m.schemas)) continue;
      for (const s of m.schemas) out.push({ moduleId: id, collection: s });
    }
    return out;
  }
}

// Singleton instance — one registry per app boot.
// Multiple instances would defeat the dependency-validation pass.
export const registry = new ModuleRegistry();
